import {array, any, node, arrayOf, bool, number, object, oneOfType, shape, string, func, oneOf} from 'prop-types';
import {boolean} from 'zod';

const assistantShape = shape({
  id: string.isRequired,
  name: string.isRequired,
  description: string.isRequired,
  role: string.isRequired,
  url_photo: string.isRequired
});

const userShape = shape({
  is_owner: number.isRequired,
  user_id: string,
  username: string.isRequired
});

const textPropTypes = shape({
  annotations: arrayOf(string),
  value: string.isRequired
});

const messageShape = shape({
  assistant_id: string,
  content: arrayOf(
    shape({
      type: string.isRequired,
      text: textPropTypes.isRequired
    })
  ).isRequired,
  created_at: number.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  file_ids: array,
  id: string,
  // eslint-disable-next-line react/forbid-prop-types
  metadata: object.isRequired,
  object: string.isRequired,
  role: string.isRequired,
  run_id: string,
  thread_id: string.isRequired
});

const summaryShape = shape({
  themes: string.isRequired,
  summaries: string.isRequired
});

const fyShape = shape({
  fy: string.isRequired
});

const projectShape = shape({
  project_name: string.isRequired,
  siren: string.isRequired,
  url_logo: string,
  connector: string.isRequired,
  first_fiscal_month: number,
  is_owner: bool.isRequired,
  schema_name: string.isRequired,
  fy_list: arrayOf(string),
  summaries: arrayOf(summaryShape),
  users: arrayOf(userShape),
  threshold: number,
  auto_update: bool,
  masked_owner_role: bool,
  last_piece_date: string.isRequired,
  last_update: string.isRequired
});

const reportShape = shape({
  report_name: string.isRequired,
  report_id: string.isRequired,
  isCustomModel: bool.isRequired
});

const workspaceShape = shape({
  workspace_name: string.isRequired,
  workspace_id: string.isRequired
});

const workingDayTranscriptionDataShape = shape({
  CodeClient: oneOfType([string, arrayOf(string)]).isRequired,
  CodeCollaborateur: string.isRequired,
  CodeMission: string.isRequired,
  CodeTache: string.isRequired,
  DateSaisie: string.isRequired,
  Intitulé: string.isRequired,
  Libelle: string.isRequired,
  NbUO: number.isRequired,
  PV1: number.isRequired,
  PV2: number.isRequired,
  PV3: number.isRequired
});

const threadShape = shape({
  thread_id: string.isRequired,
  thread_name: string.isRequired,
  creation_time: number.isRequired
});

const connectorShape = shape({
  category: string.isRequired,
  id: string.isRequired,
  is_active: bool.isRequired,
  is_beta: bool.isRequired,
  is_manual: bool.isRequired,
  is_local: bool,
  max_folder_to_load: number,
  name: string.isRequired,
  open_on_click: string.isRequired,
  url_favicon: string,
  url_help: string,
  url_logo: string,
  credential_fields: arrayOf(shape({}))
});

const singleModuleShape = shape({
  name: string.isRequired,
  id: string.isRequired,
  requiresSirenSelected: bool
});

const moduleCategoryShape = shape({
  label: string.isRequired,
  id: string.isRequired,
  modules: arrayOf(singleModuleShape).isRequired
});

const onboardingFlowStepShape = shape({
  step_id: number.isRequired,
  order: number.isRequired,
  title: string.isRequired,
  description: string.isRequired,
  url_video: string,
  position: number.isRequired
});

const onboardingFlowShape = shape({
  id: number.isRequired,
  order: number.isRequired,
  title: string.isRequired,
  description: string.isRequired,
  for_guest_user: bool.isRequired,
  icon: string.isRequired,
  steps: arrayOf(onboardingFlowStepShape)
});

const flowStepShape = shape({
  step_id: number.isRequired,
  order: number.isRequired,
  title: string.isRequired,
  description: string.isRequired,
  url_video: string,
  position: number.isRequired
});

const flowShape = shape({
  id: number.isRequired,
  title: string.isRequired,
  description: string.isRequired,
  icon: string.isRequired,
  for_guest_user: bool.isRequired,
  show_on_welcome: bool.isRequired,
  steps: arrayOf(flowStepShape).isRequired
});

const ressourceSingleLinkShape = shape({
  subtitle: string,
  src_iframe: string.isRequired,
  link: string.isRequired
});

const ressourceShape = shape({
  id: string.isRequired,
  title: string.isRequired,
  links: arrayOf(ressourceSingleLinkShape),
  icon: node.isRequired
});

// Payment page product
const marketingFeatureShape = shape({
  name: string.isRequired
});

const tierShape = shape({
  flat_amount: number, // nullable, so optional
  flat_amount_decimal: string, // nullable, optional
  unit_amount: number.isRequired,
  unit_amount_decimal: string.isRequired,
  up_to: number // nullable, optional
});

const productShape = shape({
  id: string.isRequired,
  name: string.isRequired,
  description: string.isRequired,
  marketing_features: arrayOf(marketingFeatureShape).isRequired,
  quantity_field_label: string.isRequired,
  quantity_field_error_text: string.isRequired,
  price_id: string.isRequired,
  billing_scheme: string.isRequired,
  tiers_mode: string,
  tiers: arrayOf(tierShape),
  transform_quantity: object, // optional, nullable
  amount: number, // optional, nullable
  // currency: string.isRequired,
  interval: string.isRequired,
  order: number.isRequired
});

const controlPropType = shape({
  register: func,
  setValue: func,
  getValues: func,
  watch: func,
  control: object,
  formState: shape({
    errors: object,
    isSubmitting: bool,
    isValid: bool
  })
});

const fieldPropType = shape({
  name: string.isRequired,
  label: string,
  placeholder: string,
  type: oneOf(['text', 'number', 'email', 'checkbox', 'select', 'radio', 'password', 'date']),
  defaultValue: any,
  disabled: bool,
  required: bool,
  helperText: string,
  options: arrayOf(
    shape({
      label: string,
      value: any
    })
  ), // for selects/radios
  validation: object // yup/zod rules or custom validators
});

const secretShape = shape({
  secret_name: string.isRequired,
  folders: string.isRequired,
  is_editable: bool.isRequired
});

const reportSingleUpdateHistoryShape = shape({
  endTime: string.isRequired,
  refreshAttempts: arrayOf(shape({})),
  refreshId: number,
  refreshType: string,
  startTime: string.isRequired,
  status: string.isRequired
});

const sourceShape = shape({
  available: bool.isRequired,
  connection_details: string.isRequired,
  connector_name: string.isRequired,
  credentials: arrayOf(string).isRequired,
  datasourceType: string.isRequired,
  gatewayId: string.isRequired,
  id: string.isRequired,
  logo: string.isRequired,
  message: string
});

export {
  moduleCategoryShape,
  threadShape,
  onboardingFlowShape,
  assistantShape,
  userShape,
  messageShape,
  projectShape,
  reportShape,
  workspaceShape,
  workingDayTranscriptionDataShape,
  connectorShape,
  flowShape,
  onboardingFlowStepShape,
  ressourceShape,
  productShape,
  controlPropType,
  fieldPropType,
  secretShape,
  reportSingleUpdateHistoryShape,
  sourceShape
};
